import { useCallback, useEffect, useSyncExternalStore } from "react"

import { isAppSurface } from "@/lib/app-surfaces"

export type Appearance = "light" | "dark" | "system"

const prefersDark = () => {
  if (typeof window === "undefined") {
    return false
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

const applyTheme = (appearance: Appearance) => {
  // The appearance preference only governs app surfaces. The marketing site
  // is always light by design; flipping it wholesale to the Dark Lime theme
  // breaks pages composed for the light surface.
  const isApp = isAppSurface(window.location.pathname)
  const isDark =
    isApp &&
    (appearance === "dark" || (appearance === "system" && prefersDark()))

  // App surfaces also swap to the neutral internal-tool skin (see
  // styles/app-theme.css); the brand tokens stay marketing-only.
  document.documentElement.classList.toggle("theme-app", isApp)
  document.documentElement.classList.toggle("dark", isDark)
  document.documentElement.style.colorScheme = isDark ? "dark" : "light"
}

/** Re-applies the stored appearance for the current route. Call after
 *  client-side navigation, where the app/marketing boundary may be crossed. */
export function syncTheme() {
  const saved = (localStorage.getItem("appearance") as Appearance) || "system"
  applyTheme(saved)
}

const mediaQuery = () => {
  if (typeof window === "undefined") {
    return null
  }

  return window.matchMedia("(prefers-color-scheme: dark)")
}

const handleSystemThemeChange = () => {
  syncTheme()
}

export function initializeTheme() {
  syncTheme()

  mediaQuery()?.addEventListener("change", handleSystemThemeChange)
}

// localStorage is the store; the server snapshot keeps SSR and the client's
// first render in agreement ("system") until hydration completes.
const appearanceListeners = new Set<() => void>()

const subscribeAppearance = (callback: () => void) => {
  appearanceListeners.add(callback)
  return () => {
    appearanceListeners.delete(callback)
  }
}

const getAppearanceSnapshot = () =>
  (localStorage.getItem("appearance") as Appearance | null) ?? "system"

const getAppearanceServerSnapshot = (): Appearance => "system"

export function useAppearance() {
  const appearance = useSyncExternalStore(
    subscribeAppearance,
    getAppearanceSnapshot,
    getAppearanceServerSnapshot,
  )

  const updateAppearance = useCallback((mode: Appearance) => {
    if (mode === "system") {
      localStorage.removeItem("appearance")
    } else {
      localStorage.setItem("appearance", mode)
    }
    applyTheme(mode)
    appearanceListeners.forEach((listener) => listener())
  }, [])

  useEffect(() => {
    applyTheme(appearance)

    return () =>
      mediaQuery()?.removeEventListener("change", handleSystemThemeChange)
  }, [appearance])

  return { appearance, updateAppearance } as const
}
